import './BurgerButton.scss';
import {useEffect, useRef, useState} from "react";
import {Link, useLocation} from "react-router-dom";
import {PATHS_API} from "../../../../../../../enum";
import {PATHS} from "../../../../../../routers/utils/Paths.links";
import {useUserStore} from "../../../../../../../modules/shop/user/store";
import {useI18n} from "../../../../../../i18n/I18nProvider";
import {useIsAdmin} from "../../../../../../hooks/useIsAdmin";
import {useHasPersonalProducts} from "../../../../../../hooks/useHasPersonalProducts";
import {CHILD_PROFILE_PATHS, dataForProfile, statsMenuItem} from "../../../../../ProfilePage/components/Sidebar/Sidebar";
import {NAV_ITEMS} from "../../navItems";

// Мобільне меню: картка гравця, основні розділи плитками, пункти профілю, вхід / вихід — однакове на всіх сторінках
const BurgerButton = () => {
    const { t } = useI18n();
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const rootRef = useRef<HTMLDivElement>(null);

    const user = useUserStore((s) => s.user);
    const loggedIn = !!user?.steamId;
    const isAdmin = useIsAdmin(loggedIn);
    const hasPersonalProducts = useHasPersonalProducts();

    // закриваємо при переході на іншу сторінку
    useEffect(() => { setOpen(false); }, [location.pathname]);

    // закриваємо кліком поза меню та клавішею Escape
    useEffect(() => {
        if (!open) return;
        const onClick = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    // статистика в меню профілю живе в розділі «Обліковий запис», якого в бургері немає, тож додаємо її сюди
    const profileItems = [...dataForProfile, statsMenuItem].filter((item) =>
        item.to !== CHILD_PROFILE_PATHS.PERSONAL_PRODUCTS || hasPersonalProducts
    );

    return (
        <div ref={rootRef} className="burger">
            <button
                type="button"
                className={`burger-btn ${open ? "is-open" : ""}`}
                onClick={() => setOpen(!open)}
                aria-label={open ? "Закрити меню" : "Відкрити меню"}
                aria-expanded={open}
            >
                <span /><span /><span />
            </button>

            <div className={`burger-panel ${open ? "is-open" : ""}`} aria-hidden={!open}>
                { loggedIn &&
                    <div className="burger-user">
                        <Link className="burger-user-link" to={PATHS.PROFILE + CHILD_PROFILE_PATHS.BASKET}>
                            { user.avatarUrl
                                ? <img src={user.avatarUrl} alt="" />
                                : <span className="burger-user-empty">{user.personaName?.[0]?.toUpperCase() ?? "?"}</span>
                            }
                            <div className="burger-user-info">
                                <b>{user.personaName}</b>
                                <span>{t("nav.profile")}</span>
                            </div>
                        </Link>
                        <Link className="burger-balance" to={PATHS.DEPOSIT}>
                            {Number(user.balance ?? 0).toFixed(2)} ₴
                        </Link>
                    </div>
                }

                <div className="burger-tiles">
                    { NAV_ITEMS.map((item, i) => {
                        const active = item.isActive(location.pathname);
                        return (
                            <Link
                                key={item.key}
                                to={item.to}
                                className={`burger-tile ${active ? "is-active" : ""}`}
                                style={{ "--i": i } as React.CSSProperties}
                                aria-current={active ? "page" : undefined}
                            >
                                {item.icon}
                                <span>{t(item.labelKey)}</span>
                            </Link>
                        );
                    })}
                </div>

                { loggedIn &&
                    <div className="burger-list">
                        { profileItems.map((item) => {
                            const to = PATHS.PROFILE + item.to;
                            return (
                                <Link key={item.to} to={to} className={`burger-list-item ${location.pathname === to ? "is-active" : ""}`}>
                                    {item.text}
                                </Link>
                            );
                        })}
                    </div>
                }

                { isAdmin &&
                    <div className="burger-list">
                        <Link to={PATHS.ADMIN} className={`burger-list-item ${location.pathname.startsWith(PATHS.ADMIN) ? "is-active" : ""}`}>
                            Адмінка
                        </Link>
                    </div>
                }

                { loggedIn
                    ? <a className="burger-auth burger-auth-out" href={PATHS_API.LOGOUT}>{t("nav.logout")}</a>
                    : <a className="burger-auth" href={PATHS_API.LOGIN}>{t("nav.login")}</a>
                }
            </div>
        </div>
    );
};

export default BurgerButton;
